<!-- Popup: TOC(Table of Contents) -->
<script lang="ts" setup>
import { computed, inject, nextTick, ref, watch } from 'vue'
import { useScroll } from '@vueuse/core'
import BasicPopup from './BasicPopup.vue'
import { superstoryTrack } from '@/track'
import { api } from '@/api'
import { syncChapterListWithAudioStore, useLoadingStore } from '@/stores'
import { CHAPTER_LOCK } from '@/constants'
import { useElementClick } from '@/composables/tracks'
import { useI18n } from '@/composables/useI18n'
import { formatUpdateTime } from '@/utils'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { features } from '@/config/brands'

const props = withDefaults(
  defineProps<{
    value: boolean
    inline?: boolean
    book: any
    contentId: string
    chapterId?: string
    checkPassGotoChapter?: (index: number) => boolean
    lockIconColor?: string // 锁图标颜色,由父组件传入
    routeName?: string // 跳转路由名称，默认为 'chapter'
  }>(), {
    inline: false,
    checkPassGotoChapter: () => true,
    routeName: READER_ROUTE_NAME,
  },
)

defineEmits<{
  (event: 'input', value: boolean): void
}>()

const LONG_BOOK_CHAPTER_THRESHOLD = 25

const theme = inject<any>('theme', { background: 'white' })

// 目录分隔线颜色：弹窗经 portal 传送到根节点，脱离了 .color-XXXX 主题包裹层，
// 拿不到 scss 里定义的 --toc-border-color，会回退成接近白色的默认值。
// 这里根据当前阅读主题背景，自行计算一条与主题同色系、低透明度、不突兀的分隔线。
function getThemeBg(): string {
  return theme?.value?.background ?? theme?.background ?? '#FFFFFF'
}
function isDarkBg(bg: string): boolean {
  const hex = bg?.replace('#', '')
  if (!hex || hex.length < 6)
    return false
  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)
  // 感知亮度，低于阈值视为深色主题
  return (0.299 * r + 0.587 * g + 0.114 * b) < 80
}
const tocBorderColor = computed(() => {
  return isDarkBg(getThemeBg()) ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'
})
const isDarkTheme = computed(() => isDarkBg(getThemeBg()))

const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const { t } = useI18n()

const chapterList = ref<GetContentGetcontentchapterbooklistResponse['data']['records']>([])
const chapterScrollRef = ref()
const { y: chapterScrollY } = useScroll(chapterScrollRef, { behavior: 'smooth' })
const chooseChapterOpitons = ref(false)
const chapterOptions = ref<any[]>([])
const chapterTotal = ref<number>(0)
const inlineChapterListLimit = computed(() => props.inline ? features?.inlineChapterListLimit : undefined)
const pagination = ref({
  pageIndex: 1,
  pageSize: 100,
})
const showUpdateTime = computed(() => chapterTotal.value > LONG_BOOK_CHAPTER_THRESHOLD)

const loadFailed = ref(false)
async function fetchChapterList() {
  loadFailed.value = false
  $showLoading()
  try {
    const chapterPagination = inlineChapterListLimit.value
      ? { pageIndex: 1, pageSize: inlineChapterListLimit.value }
      : pagination.value
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', { params: { contentId: props.contentId, ...chapterPagination } })
    chapterList.value = chapterListData.records
    chapterTotal.value = chapterListData.total
    // 同步听书播放器中已解锁章节状态，避免目录页显示锁定
    syncChapterListWithAudioStore(props.contentId, chapterList.value)
    const page = chapterListData.pages
    // Note: 章节分页 options
    chapterOptions.value = Array.from({ length: inlineChapterListLimit.value ? 0 : page })
      .map((_, index) => ({
        label: `${(index * pagination.value.pageSize + 1)} - ${(index + 1) * pagination.value.pageSize}`,
        value: index + 1,
      }))
    // Note: auto scroll to current chapterId
    if (props.chapterId) {
      const itemHeight = showUpdateTime.value ? 65 : 57
      const index = chapterList.value.findIndex(item => item.id === props.chapterId)
      nextTick(() => {
        if (index > -1)
          chapterScrollY.value = index * itemHeight
      })
    }
  }
  catch (error) {
    if (!props.inline)
      throw error
    loadFailed.value = true
  }
  finally {
    $hideLoading()
  }
}
async function onOpen() {
  await fetchChapterList()
}

function selectChapter(item: any) {
  pagination.value.pageIndex = item.value
}

function toChapter($event: Event, item: any, navigate: any) {
  superstoryTrack('chapter_ck', { book_id: props.contentId, book_name: props.book?.contentName, chapter_id: item.id, chapter_name: item.chapterName, is_chargeable: item.price > 0, is_unlocked: item.lock === CHAPTER_LOCK.UNLOCK })
  if (props.checkPassGotoChapter(item.sequenceNo))
    navigate($event)
}

watch(() => pagination.value.pageIndex, async () => {
  await fetchChapterList()
  chooseChapterOpitons.value = false
})

watch(
  () => props.value,
  (val) => {
    if (val)
      elementClick('catalogue')
  },
)
watch(() => [props.inline, props.contentId], () => {
  if (props.inline) {
    elementClick('catalogue')
    pagination.value.pageIndex = 1
    onOpen()
  }
}, { immediate: true })
</script>

<template>
  <!-- Popup: 查看章节 -->
  <component :is="inline ? 'div' : BasicPopup" :class="{ 'toc-inline': inline }" :value="value" @input="$emit('input', $event)" @open="onOpen">
    <div
      v-if="book"
      class="max-h-90dvh overflow-scroll rounded-t"
      :class="{ 'toc-dark': isDarkTheme }"
      :style="`background: ${theme ? getThemeBg() : ''}; --toc-border-color: ${tocBorderColor}`"
    >
      <div v-if="!inline" class="toc-header pt-5 pb-13px px-5 bg-b-light">
        <div class="flex space-x-10px">
          <img class="w-55px h-77px rounded-3px" :src="book.contentCoverUrl">
          <div class="flex flex-col justify-center">
            <p class="text-17px font-bold line-clamp-2">
              {{ book.contentName }}
            </p>
            <p class="mt-2 text-13px text-n-text-3">
              {{ chapterTotal }} {{ t('episodes') }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="inline && (chapterOptions.length > 1 || loadFailed || chapterTotal === 0)" class="px-5 py-3">
        <select v-if="chapterOptions.length > 1" v-model="pagination.pageIndex" :aria-label="t('Catalogue')" class="w-full p-2 bg-n-bg-card border rounded">
          <option v-for="option in chapterOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <button v-if="loadFailed" type="button" class="text-n-primary" @click="onOpen">
          {{ t('Retry') }}
        </button>
        <p v-else-if="chapterTotal === 0">
          {{ t('NoChapters') }}
        </p>
      </div>
      <ul v-show="chooseChapterOpitons" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <li
          v-for="item in chapterOptions" :key="item.value"
          class="toc-item h-57px lh-57px flex items-center justify-between"
          @click="selectChapter(item)"
        >
          <span :class="{ 'text-n-primary! font-bold': pagination.pageIndex === item.value }">{{ item.label }}</span>
          <i-assets-icon-check v-if="pagination.pageIndex === item.value" class="w-17px" />
        </li>
      </ul>
      <ul v-show="!chooseChapterOpitons" ref="chapterScrollRef" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <router-link
          v-for="item in chapterList" :key="item.id"
          v-slot="{ navigate }" custom
          :to="{ name: routeName, params: { contentId, chapterId: item.id }, query: { skipPosition: '1' } }" replace
        >
          <li
            class="toc-item flex items-center justify-between"
            :class="showUpdateTime ? 'h-65px' : 'h-57px lh-57px'"
            @click="toChapter($event, item, navigate)"
          >
            <div v-if="showUpdateTime" class="flex-1 min-w-0">
              <div class="text-truncate" :class="{ 'text-n-primary! font-bold': chapterId === item.id }">
                {{ item.chapterName }}
              </div>
              <div class="mt-2 text-11px lh-12px text-n-text-3">
                {{ formatUpdateTime(item.updateDateTime) }}
              </div>
            </div>
            <span v-else class="flex-1 text-truncate" :class="{ 'text-n-primary! font-bold': chapterId === item.id }">{{ item.chapterName }}</span>
            <i-assets-icon-lock
              v-if="item.lockStatus === CHAPTER_LOCK.LOCK"
              class="w-17px toc-lock-icon"
              :style="lockIconColor ? { color: lockIconColor } : undefined"
            />
          </li>
        </router-link>
      </ul>
    </div>
  </component>
</template>

<style scoped>
.toc-inline > div { max-height: none; border-radius: 0; }
.toc-inline ul { height: auto; overflow: visible; }

.toc-lock-icon {
  /* 默认使用 CSS 变量,如果没有则使用默认黑色 */
  color: var(--toc-lock-color, rgba(0, 0, 0, 0.3));
}

.toc-item {
  /* 章节分隔线 - 使用主题配置的颜色 */
  border-bottom: 1px solid var(--toc-border-color, #ECEDEF);
}

.toc-dark {
  color: #AAAAAA;
}

.toc-dark .toc-header {
  background: #313030;
}
</style>
